/**
 * La reprise des données du mode `?demo` (ARTO-126) : un mandat par galerie et
 * un lot dans chaque état, en mémoire, pour vérifier l'onglet « Reprise » sans
 * API. Aucune vraie galerie ni personne réelle : des intitulés inventés.
 *
 * Les règles de l'API que l'écran affiche sont rejouées avec leurs codes
 * (`docs/dev/api.md` § 12.13) : `mandat_absent`, `nature_hors_mandat`,
 * `lot_non_pret`, `essai_en_cours`, `lot_deja_lance`, et l'élévation du second
 * facteur pour les factures antérieures et les documents confidentiels.
 *
 * Un lot déposé ou lancé ici avance avec le temps (essai, file, travail,
 * rapport) ; les lots de départ restent figés dans leur état. Un fichier dont
 * le nom contient « refus » donne un essai avec des lignes refusées.
 */
import { ApiError } from './api.js';
import { demoCorrespondance } from './demoCorrespondance.js';
import { compteDemoCourant, demoApi } from './demoData.js';
import { demoDepots } from './demoDepots.js';
import { galerieDeDossierEnReprise } from './demoOuvertures.js';
import type { CreationLotFichiers } from './images.js';
import { exigerElevationDemo } from './demoSaisies.js';
import type { RapportAnnulation } from './plan.js';
import {
  CHAMPS_PAR_NATURE,
  exigeSecondFacteur,
  normaliserRapport,
  type AvancementTravail,
  type ChampLot,
  type DemandeMandat,
  type DepotLot,
  type EtatMandat,
  type Gabarit,
  type LotReprise,
  type MandatVue,
  type NatureLot,
  type PoseMandat,
  type RapportLot,
} from './reprise.js';

const JOUR = 86_400_000;
const HEURE = 3_600_000;

function il_y_a(ms: number): string {
  return new Date(Date.now() - ms).toISOString();
}

function dans(ms: number): string {
  return new Date(Date.now() + ms).toISOString();
}

function operateurCourant(): string {
  return compteDemoCourant().compte.email;
}

// ── Rapports de départ ───────────────────────────────────────────────────

interface Chiffres {
  total: number;
  crees: number;
  completes?: number;
  ignores?: number;
  refuses?: number;
}

/** Un rapport au format des natures du socle (`RapportReprise`). */
function rapportNature(chiffres: Chiffres, essai: boolean, plus: object = {}): object {
  return {
    essai,
    total: chiffres.total,
    crees: chiffres.crees,
    completes: chiffres.completes ?? 0,
    ignores: chiffres.ignores ?? 0,
    refuses: chiffres.refuses ?? 0,
    colonnesInconnues: [],
    erreurs: [],
    avertissements: [],
    details: {},
    listes: {},
    ...plus,
  };
}

/** Un rapport au format de l'import d'œuvres (`RapportImport`) : sa propre forme. */
function rapportOeuvres(total: number, refuses: number, essai: boolean): object {
  return {
    essai,
    total,
    crees: total - refuses,
    refuses,
    artistesCrees: ['Margaux Delorme', 'Inès Kowalczyk'],
    imagesDeposees: essai ? 0 : total - refuses - 3,
    imagesNonDeposees: 0,
    colonnesInconnues: ['Commentaire interne'],
    erreurs: Array.from({ length: refuses }, (_, i) => ({
      ligne: 14 + i * 9,
      titre: i === 0 ? 'Étude pour un paysage' : 'Sans titre (série bleue)',
      erreurs: [
        i === 0 ? 'Le lieu « Réserve nord » n’existe pas.' : 'L’année « XX » est illisible.',
      ],
    })),
    avertissements: [
      { ligne: 31, titre: 'Nature morte aux coings', erreurs: ['Doublon probable de la ligne 7.'] },
    ],
  };
}

const ESSAI_EXPOSITIONS_REFUS = rapportNature({ total: 40, crees: 36, refuses: 4 }, true, {
  colonnesInconnues: ['Commentaire interne'],
  erreurs: [
    {
      ligne: 12,
      libelle: 'Hiver blanc',
      messages: ['La date de fin précède la date de début.'],
    },
    { ligne: 19, libelle: 'Rétrospective Lenoir', messages: ['Le lieu est obligatoire.'] },
    {
      ligne: 27,
      libelle: 'Foire du printemps',
      messages: ['Le type « salon » n’existe pas : foire, exposition ou événement.'],
    },
    { ligne: 33, libelle: 'Cabinet de dessins', messages: ['La date de début est illisible.'] },
  ],
  avertissements: [
    { ligne: 8, libelle: 'Paysages d’été', messages: ['Une exposition porte déjà ce titre.'] },
  ],
});

// ── État ─────────────────────────────────────────────────────────────────

/** Ce que la démonstration retient d'un lot en plus de sa vue. */
export interface LotDemo {
  vue: LotReprise;
  /** Rapports bruts, tels que le travail les dépose. */
  essai: object | null;
  final: object | null;
  /** « refus » : l'essai de ce lot trouvera des lignes refusées. */
  profil: 'propre' | 'refus';
  /** Début du travail en cours, pour le faire avancer. */
  phase: { genre: 'essai' | 'import' | 'annulation'; depuis: number } | null;
  /** Le rapport de l'annulation faite, tel que l'API le rend. */
  rapportAnnulation: RapportAnnulation | null;
  /** Un import qui n'en finit pas, pour montrer une barre qui avance (démonstration d'un plan). */
  defile: { base: number; parSeconde: number; depuis: number } | null;
}

interface Galerie {
  mandat: EtatMandat;
  lots: LotDemo[];
}

let galeries: Map<string, Galerie> | null = null;
let compteur = 0;

function nouvelId(prefixe: string): string {
  compteur += 1;
  return `${prefixe}-${compteur}`;
}

function mandatVue(sur: Partial<MandatVue> & Pick<MandatVue, 'etat' | 'source'>): MandatVue {
  return {
    id: nouvelId('demo-mandat'),
    natures: ['oeuvres', 'contacts', 'expositions', 'documents', 'images'],
    confidentiels: false,
    reference: '',
    motif: 'Reprise du fonds depuis l’ancien logiciel',
    dureeJours: null,
    demandePar: null,
    accordePar: null,
    demandeLe: il_y_a(12 * JOUR),
    accordeLe: null,
    expireLe: dans(30 * JOUR),
    revoqueLe: null,
    revoqueDepuis: null,
    ...sur,
  };
}

interface SemisLot {
  /** Un identifiant stable, pour qu'un plan de démonstration cite ce lot. */
  id?: string;
  nature: NatureLot;
  etat: LotReprise['etat'];
  fichier: string;
  octets: number;
  lignes: number;
  compteurs?: Chiffres;
  essai?: object;
  final?: object;
  confidentiels?: boolean;
  travail?: LotReprise['travail'];
  creeIlYA: number;
  /** Ce que la vue porte en plus : écartement, annulation, scellement, rejets. */
  plus?: Partial<LotReprise>;
  rapportAnnulation?: RapportAnnulation;
  defile?: LotDemo['defile'];
}

function lotDeDepart(g: string, semis: SemisLot): LotDemo {
  const operateur = 'exploitation@venio.test';
  const compteurs = semis.compteurs
    ? {
        total: semis.compteurs.total,
        crees: semis.compteurs.crees,
        completes: semis.compteurs.completes ?? 0,
        ignores: semis.compteurs.ignores ?? 0,
        refuses: semis.compteurs.refuses ?? 0,
        ecartes: 0,
      }
    : null;
  const champsAnnexes = (
    (
      CHAMPS_PAR_NATURE as Partial<
        Record<NatureLot, readonly { champ: ChampLot; requis: boolean }[]>
      >
    )[semis.nature] ?? []
  )
    .slice(1)
    .filter((c) => c.requis);
  return {
    vue: {
      id: semis.id ?? nouvelId('demo-lot'),
      galerieId: g,
      nature: semis.nature,
      etat: semis.etat,
      revision: 1,
      sources: [
        { champ: 'fichier', nom: semis.fichier, octets: semis.octets },
        ...champsAnnexes.map((c) => ({
          champ: c.champ,
          nom: c.champ === 'pdfs' ? 'factures-pdf.zip' : 'pieces-du-coffre.zip',
          octets: 42_000_000,
        })),
      ],
      options: { confidentiels: semis.confidentiels ?? false, motifInventaire: null },
      lignes: semis.lignes,
      compteurs,
      rapportEssai: semis.essai !== undefined,
      rapport: semis.final !== undefined,
      travail: semis.travail ?? null,
      operateur,
      creeLe: il_y_a(semis.creeIlYA),
      essayeLe: semis.essai ? il_y_a(semis.creeIlYA - 10 * 60_000) : null,
      lanceLe: ['enfile', 'en_cours', 'termine', 'echoue'].includes(semis.etat)
        ? il_y_a(semis.creeIlYA - 30 * 60_000)
        : null,
      termineLe: ['termine', 'echoue'].includes(semis.etat)
        ? il_y_a(Math.max(semis.creeIlYA - 45 * 60_000, 60_000))
        : null,
      ...semis.plus,
    },
    essai: semis.essai ?? null,
    final: semis.final ?? null,
    profil: semis.essai === ESSAI_EXPOSITIONS_REFUS ? 'refus' : 'propre',
    phase: null,
    rapportAnnulation: semis.rapportAnnulation ?? null,
    defile: semis.defile ?? null,
  };
}

function travail(
  etat: string,
  type: string,
  tentatives = 1,
  plus: Partial<NonNullable<LotReprise['travail']>> = {},
): LotReprise['travail'] {
  return {
    id: nouvelId('demo-travail'),
    type,
    etat,
    tentatives,
    maxTentatives: 3,
    termineLe: null,
    ...plus,
  };
}

/** L'avancement d'un travail au rythme constaté : le temps restant en découle. */
function avancement(
  etape: string,
  faits: number,
  total: number,
  ecouleMs: number,
): AvancementTravail {
  return {
    etape,
    faits,
    total,
    maj: new Date().toISOString(),
    restantEstimeMs:
      faits <= 0 || faits >= total ? null : Math.round((ecouleMs / faits) * (total - faits)),
  };
}

/** Une galerie née d'un dossier d'ouverture a le mandat de son contrat ; les autres, aucun. */
function mandatDuDossier(idGalerie: string): EtatMandat {
  if (!galerieDeDossierEnReprise(idGalerie)) return { actif: null, demande: null };
  return {
    actif: mandatVue({
      etat: 'actif',
      source: 'contrat',
      reference: 'BC-2026-41',
      accordePar: 'direction@venio.test',
      accordeLe: il_y_a(6 * JOUR),
      expireLe: dans(54 * JOUR),
      motif: 'Mandat prévu au contrat d’ouverture',
    }),
    demande: null,
  };
}

function semer(): Map<string, Galerie> {
  const liste = demoApi.listerGaleries();
  const resultat = new Map<string, Galerie>();
  liste.forEach((galerie, rang) => {
    const mandat: EtatMandat =
      rang === 0
        ? {
            actif: mandatVue({
              etat: 'actif',
              source: 'contrat',
              reference: 'BC-2026-118',
              confidentiels: true,
              accordePar: 'direction@venio.test',
              accordeLe: il_y_a(9 * JOUR),
              expireLe: dans(41 * JOUR),
              motif: 'Mandat prévu au contrat d’ouverture',
            }),
            demande: null,
          }
        : rang === 1
          ? {
              actif: null,
              demande: mandatVue({
                etat: 'en_attente',
                source: 'galerie',
                natures: ['oeuvres', 'contacts'],
                dureeJours: 14,
                demandePar: 'exploitation@venio.test',
                demandeLe: il_y_a(2 * HEURE),
                expireLe: dans(7 * JOUR - 2 * HEURE),
              }),
            }
          : mandatDuDossier(galerie.id);
    const g = galerie.id;
    const lots =
      rang === 0
        ? [
            lotDeDepart(g, {
              nature: 'oeuvres',
              etat: 'enfile',
              fichier: 'inventaire-fonds.xlsx',
              octets: 212_000,
              lignes: 640,
              compteurs: { total: 640, crees: 640 },
              essai: rapportOeuvres(640, 0, true),
              travail: travail('en_attente', 'import:oeuvres'),
              creeIlYA: 25 * 60_000,
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-documents',
              nature: 'documents',
              etat: 'pret',
              fichier: 'manifeste-coffre.csv',
              octets: 18_400,
              lignes: 128,
              compteurs: { total: 128, crees: 120, ignores: 8 },
              essai: rapportNature({ total: 128, crees: 120, ignores: 8 }, true),
              confidentiels: true,
              travail: travail('reussi', 'reprise:essayer'),
              creeIlYA: 3 * HEURE,
            }),
            lotDeDepart(g, {
              nature: 'expositions',
              etat: 'essaye',
              fichier: 'expositions-passees.xlsx',
              octets: 36_000,
              lignes: 40,
              compteurs: { total: 40, crees: 36, refuses: 4 },
              essai: ESSAI_EXPOSITIONS_REFUS,
              travail: travail('reussi', 'reprise:essayer'),
              creeIlYA: 5 * HEURE,
              plus: { rejets: [{ feuille: 'principal', lignes: 4 }] },
            }),
            // Le plan de démonstration : étapes 1 et 2 faites, l'étape 3 tourne, le reste est prêt.
            lotDeDepart(g, {
              id: 'demo-lot-plan-lieux',
              nature: 'lieux',
              etat: 'termine',
              fichier: 'lieux-galerie.csv',
              octets: 2_400,
              lignes: 18,
              compteurs: { total: 18, crees: 18 },
              essai: rapportNature({ total: 18, crees: 18 }, true),
              final: rapportNature({ total: 18, crees: 18 }, false),
              travail: travail('reussi', 'import:lieux'),
              creeIlYA: 2 * HEURE + 20 * 60_000,
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-artistes',
              nature: 'artistes',
              etat: 'termine',
              fichier: 'artistes-fonds.csv',
              octets: 9_100,
              lignes: 24,
              compteurs: { total: 24, crees: 21, completes: 3 },
              essai: rapportNature({ total: 24, crees: 21, completes: 3 }, true),
              final: rapportNature({ total: 24, crees: 21, completes: 3 }, false),
              travail: travail('reussi', 'import:artistes'),
              creeIlYA: 2 * HEURE + 18 * 60_000,
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-images',
              nature: 'images',
              etat: 'en_cours',
              fichier: 'images-oeuvres.zip',
              octets: 640_000_000,
              lignes: 412,
              compteurs: { total: 412, crees: 412 },
              essai: rapportNature({ total: 412, crees: 412 }, true),
              travail: travail('en_cours', 'import:images', 1, {
                avancement: avancement('import', 187, 412, 24_000),
              }),
              creeIlYA: 2 * HEURE,
              defile: { base: 187, parSeconde: 6, depuis: Date.now() },
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-contacts',
              nature: 'contacts',
              etat: 'pret',
              fichier: 'carnet-vip.csv',
              octets: 14_200,
              lignes: 120,
              compteurs: { total: 120, crees: 117, refuses: 3 },
              essai: rapportNature({ total: 120, crees: 117, refuses: 3 }, true, {
                erreurs: [
                  {
                    ligne: 9,
                    libelle: 'Hélène Varga',
                    messages: ['L’adresse e-mail est illisible.'],
                  },
                  {
                    ligne: 41,
                    libelle: 'M. Roche',
                    messages: ['Le consentement doit être oui ou non.'],
                  },
                  {
                    ligne: 77,
                    libelle: 'Atelier Brun',
                    messages: ['Une valeur obligatoire est vide.'],
                  },
                ],
              }),
              travail: travail('reussi', 'reprise:essayer'),
              creeIlYA: 2 * HEURE + 5 * 60_000,
              plus: {
                compteurs: {
                  total: 120,
                  crees: 117,
                  completes: 0,
                  ignores: 0,
                  refuses: 3,
                  ecartes: 3,
                },
                rejets: [{ feuille: 'principal', lignes: 3 }],
                ecartes: {
                  nombre: 3,
                  motif:
                    'Trois fiches sans adresse exploitable : la galerie les ressaisit à la main.',
                  par: 'exploitation@venio.test',
                  le: il_y_a(95 * 60_000),
                },
              },
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-expositions',
              nature: 'expositions',
              etat: 'pret',
              fichier: 'expositions-recentes.xlsx',
              octets: 21_000,
              lignes: 22,
              compteurs: { total: 22, crees: 20, ignores: 2 },
              essai: rapportNature({ total: 22, crees: 20, ignores: 2 }, true, {
                details: { signales: 2 },
                revoir: [
                  {
                    ligne: 6,
                    libelle: 'Hiver blanc',
                    refOrigine: 'tableur-maison:E-014',
                    cibleId: '6720a1c0e3b4f2a9d1c80b31',
                  },
                  {
                    ligne: 15,
                    libelle: 'Paysages d’été',
                    refOrigine: 'tableur-maison:E-022',
                    cibleId: '6720a1c0e3b4f2a9d1c80b4a',
                  },
                ],
                avertissements: [
                  {
                    ligne: 6,
                    libelle: 'Hiver blanc',
                    messages: [
                      'Référence d’origine « tableur-maison:E-014 » : la fiche a changé dans l’ancien logiciel depuis sa reprise. Rien n’est écrasé : à revoir.',
                    ],
                  },
                  {
                    ligne: 15,
                    libelle: 'Paysages d’été',
                    messages: [
                      'Référence d’origine « tableur-maison:E-022 » : la fiche a changé dans l’ancien logiciel depuis sa reprise. Rien n’est écrasé : à revoir.',
                    ],
                  },
                  {
                    ligne: 18,
                    libelle: 'Cabinet de dessins',
                    messages: ['Le lieu « Réserve nord » est créé avec ce lot.'],
                  },
                ],
              }),
              travail: travail('reussi', 'reprise:essayer'),
              creeIlYA: 2 * HEURE,
            }),
            lotDeDepart(g, {
              nature: 'contacts',
              etat: 'depose',
              fichier: 'carnet-adresses.xlsx',
              octets: 48_000,
              lignes: 312,
              creeIlYA: 6 * HEURE,
            }),
            lotDeDepart(g, {
              nature: 'factures-historiques',
              etat: 'en_cours',
              fichier: 'factures-2019-2025.csv',
              octets: 91_000,
              lignes: 215,
              compteurs: { total: 215, crees: 215 },
              essai: rapportNature({ total: 215, crees: 215 }, true),
              travail: travail('en_cours', 'import:factures-historiques'),
              creeIlYA: 8 * HEURE,
            }),
            lotDeDepart(g, {
              nature: 'contacts',
              etat: 'termine',
              fichier: 'contacts-anciens.csv',
              octets: 52_000,
              lignes: 312,
              compteurs: { total: 312, crees: 290, completes: 14, ignores: 8 },
              essai: rapportNature({ total: 312, crees: 290, completes: 14, ignores: 8 }, true),
              final: rapportNature({ total: 312, crees: 290, completes: 14, ignores: 8 }, false, {
                details: { consentementsImportes: 118, retraitsAppliques: 9 },
              }),
              travail: travail('reussi', 'import:contacts'),
              creeIlYA: 2 * JOUR,
            }),
            lotDeDepart(g, {
              id: 'demo-lot-plan-oeuvres',
              nature: 'oeuvres',
              etat: 'termine',
              fichier: 'oeuvres-collection-privee.xlsx',
              octets: 96_000,
              lignes: 85,
              compteurs: { total: 85, crees: 85 },
              essai: rapportOeuvres(85, 0, true),
              final: rapportOeuvres(85, 0, false),
              travail: travail('reussi', 'import:oeuvres'),
              creeIlYA: 3 * JOUR,
            }),
            lotDeDepart(g, {
              nature: 'documents',
              etat: 'echoue',
              fichier: 'manifeste-contrats.csv',
              octets: 9_800,
              lignes: 64,
              compteurs: { total: 64, crees: 62, refuses: 2 },
              essai: rapportNature({ total: 64, crees: 62, refuses: 2 }, true),
              travail: travail('echoue', 'import:documents', 3, {
                raison: 'ecartement_perime',
                erreur: 'L’écartement ne décrit pas les sources de ce lot : refaites l’essai.',
              }),
              creeIlYA: 4 * JOUR,
              plus: {
                compteurs: {
                  total: 64,
                  crees: 62,
                  completes: 0,
                  ignores: 0,
                  refuses: 2,
                  ecartes: 2,
                },
                rejets: [{ feuille: 'principal', lignes: 2 }],
                ecartes: {
                  nombre: 2,
                  motif: 'Deux contrats scannés sans pièce : la galerie les redépose plus tard.',
                  par: 'exploitation@venio.test',
                  le: il_y_a(4 * JOUR + 20 * 60_000),
                },
              },
            }),
            // Un lot annulé : 2 fiches supprimées, 3 gardées pour des raisons différentes.
            lotDeDepart(g, {
              id: 'demo-lot-annule',
              nature: 'oeuvres',
              etat: 'termine',
              fichier: 'oeuvres-salon-2024.xlsx',
              octets: 21_500,
              lignes: 5,
              compteurs: { total: 5, crees: 4, completes: 1 },
              essai: rapportNature({ total: 5, crees: 4, completes: 1 }, true),
              final: rapportNature({ total: 5, crees: 4, completes: 1 }, false),
              travail: travail('reussi', 'import:oeuvres'),
              creeIlYA: 5 * JOUR,
              plus: {
                annulation: {
                  etat: 'faite',
                  demandeLe: il_y_a(2 * JOUR),
                  par: 'direction@venio.test',
                  faitLe: il_y_a(2 * JOUR - 4 * 60_000),
                  compteurs: { supprimes: 2, conserves: 3, dejaAbsents: 0, nonDefaites: 1 },
                  travail: null,
                },
              },
              rapportAnnulation: {
                version: 1,
                lotId: 'demo-lot-annule',
                importId: 'demo-import-annule',
                nature: 'oeuvres',
                travailId: 'demo-travail-annulation',
                etabliLe: il_y_a(2 * JOUR - 4 * 60_000),
                supprimes: [
                  { modele: 'Artwork', id: '6720a1c0e3b4f2a9d1c80101' },
                  { modele: 'Media', id: '6720a1c0e3b4f2a9d1c80102' },
                ],
                conserves: [
                  {
                    modele: 'Artwork',
                    id: '6720a1c0e3b4f2a9d1c80103',
                    motifs: ['modifie'],
                    ligne: 14,
                    refOrigine: 'manuel:OEU-0114',
                  },
                  {
                    modele: 'Artwork',
                    id: '6720a1c0e3b4f2a9d1c80104',
                    motifs: ['reference:reservation'],
                    ligne: 22,
                    refOrigine: 'manuel:OEU-0122',
                  },
                  {
                    modele: 'Artwork',
                    id: '6720a1c0e3b4f2a9d1c80105',
                    motifs: ['reference:autre_lot'],
                  },
                ],
                dejaAbsents: [],
                nonDefaites: 1,
                sansCible: 0,
                objetsRetires: 9,
              },
            }),
            // Un lot scellé : la reprise est figée, il ne s'annule plus.
            lotDeDepart(g, {
              id: 'demo-lot-scelle',
              nature: 'factures-historiques',
              etat: 'scelle',
              fichier: 'factures-2018.csv',
              octets: 64_000,
              lignes: 96,
              compteurs: { total: 96, crees: 96 },
              essai: rapportNature({ total: 96, crees: 96 }, true),
              final: rapportNature({ total: 96, crees: 96 }, false),
              travail: travail('reussi', 'import:factures-historiques'),
              creeIlYA: 9 * JOUR,
              plus: { scelleLe: il_y_a(JOUR) },
            }),
          ]
        : [];
    resultat.set(galerie.id, { mandat, lots });
  });
  return resultat;
}

function etat(idGalerie: string): Galerie {
  galeries ??= semer();
  const trouvee = galeries.get(idGalerie);
  if (trouvee) return trouvee;
  const vide: Galerie = { mandat: mandatDuDossier(idGalerie), lots: [] };
  galeries.set(idGalerie, vide);
  return vide;
}

function introuvable(libelle: string): ApiError {
  return new ApiError(404, 'ressource.introuvable', `${libelle} introuvable.`);
}

function conflit(raison: string, message: string): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison });
}

function refus(raison: string, message: string): ApiError {
  return new ApiError(403, 'acces.refuse', message, { raison });
}

function mandatActif(g: Galerie): MandatVue | null {
  const actif = g.mandat.actif;
  return actif && new Date(actif.expireLe).getTime() > Date.now() ? actif : null;
}

/** Le contrôle de l'enfilement : un mandat en vigueur qui couvre le lot. */
function exigerMandat(g: Galerie, lot: LotReprise): void {
  const actif = mandatActif(g);
  if (!actif)
    throw refus('mandat_absent', 'Aucun mandat de reprise en vigueur pour cette galerie.');
  if (!actif.natures.includes(lot.nature) || (lot.options.confidentiels && !actif.confidentiels))
    throw refus(
      'nature_hors_mandat',
      'Le mandat de reprise de cette galerie ne couvre pas ce lot.',
    );
}

const MODELE_DE_NATURE: Readonly<Record<NatureLot, string>> = {
  lieux: 'Location',
  artistes: 'Artist',
  oeuvres: 'Artwork',
  contacts: 'Contact',
  expositions: 'Exhibition',
  'factures-historiques': 'FactureHistorique',
  documents: 'Document',
  images: 'Media',
  localisations: 'Movement',
  provenance: 'Provenance',
  editions: 'Edition',
  membres: 'Invitation',
};

/** Ce qui désigne une fiche reprise, par nature (`reprise.md`, « Retenue par »). */
const REFERENCE_DE_NATURE: Readonly<Record<NatureLot, string>> = {
  lieux: 'reference:oeuvre',
  artistes: 'reference:oeuvre',
  oeuvres: 'reference:reservation',
  contacts: 'reference:facture',
  expositions: 'reference:actualite',
  'factures-historiques': 'modifie',
  documents: 'reference:consultation',
  images: 'reference:image_principale',
  localisations: 'modifie',
  provenance: 'modifie',
  editions: 'reference:vente',
  membres: 'acceptee',
};

function idFictif(graine: number, rang: number): string {
  return `6720a1c0e3b4f2a9${(graine * 4096 + rang).toString(16).padStart(8, '0')}`;
}

/** Le rapport d'une annulation de démonstration : presque tout part, deux fiches restent. */
function rapportAnnulationDe(lot: LotDemo): RapportAnnulation {
  const nature = lot.vue.nature;
  const compteurs = lot.vue.compteurs;
  const crees = compteurs?.crees ?? lot.vue.lignes;
  const gardees = Math.min(2, crees);
  const modele = MODELE_DE_NATURE[nature];
  const graine = lot.vue.id.length;
  return {
    version: 1,
    lotId: lot.vue.id,
    importId: `${lot.vue.id}-import`,
    nature,
    travailId: nouvelId('demo-travail'),
    etabliLe: new Date().toISOString(),
    supprimes: Array.from({ length: crees - gardees }, (_, rang) => ({
      modele,
      id: idFictif(graine, rang),
    })),
    conserves: [
      {
        modele,
        id: idFictif(graine, 4000),
        motifs: ['modifie'],
        ligne: 3,
        refOrigine: 'manuel:REF-0003',
      },
      { modele, id: idFictif(graine, 4001), motifs: [REFERENCE_DE_NATURE[nature]], ligne: 8 },
    ].slice(0, gardees),
    dejaAbsents: [],
    nonDefaites: compteurs?.completes ?? 0,
    sansCible: 0,
    objetsRetires: ['oeuvres', 'images', 'documents', 'factures-historiques'].includes(nature)
      ? (crees - gardees) * 3
      : 0,
  };
}

/** Le lot suit son travail à chaque lecture, comme l'API. */
function avancer(lot: LotDemo): void {
  if (lot.defile && lot.vue.travail) {
    // Un import qui ne finit pas : la barre avance au rythme de la démonstration.
    const ecoule = Date.now() - lot.defile.depuis;
    const total = lot.vue.lignes;
    const faits = Math.min(
      Math.floor(total * 0.97),
      lot.defile.base + Math.floor((ecoule / 1000) * lot.defile.parSeconde),
    );
    lot.vue.travail.avancement = avancement('import', faits, total, Math.max(ecoule, 24_000));
  }
  const phase = lot.phase;
  if (!phase) return;
  const ecoule = Date.now() - phase.depuis;
  if (phase.genre === 'annulation') {
    const annulation = lot.vue.annulation;
    if (!annulation) return;
    const total = lot.vue.compteurs?.crees ?? lot.vue.lignes;
    if (ecoule < 1_000) return;
    if (ecoule < 5_000) {
      annulation.etat = 'en_cours';
      const faits = Math.floor((total * (ecoule - 1_000)) / 4_000);
      annulation.travail = {
        ...(annulation.travail ?? travail('en_cours', 'reprise:annuler')!),
        etat: 'en_cours',
        avancement: avancement('annulation', faits, total, ecoule - 1_000),
      };
      return;
    }
    const rapport = rapportAnnulationDe(lot);
    lot.rapportAnnulation = rapport;
    annulation.etat = 'faite';
    annulation.faitLe = new Date().toISOString();
    annulation.compteurs = {
      supprimes: rapport.supprimes.length,
      conserves: rapport.conserves.length,
      dejaAbsents: 0,
      nonDefaites: rapport.nonDefaites,
    };
    annulation.travail = {
      ...(annulation.travail ?? travail('reussi', 'reprise:annuler')!),
      etat: 'reussi',
      avancement: null,
      termineLe: annulation.faitLe,
    };
    lot.vue.revision += 1;
    lot.phase = null;
    return;
  }
  if (phase.genre === 'essai') {
    if (ecoule < 800) return;
    if (ecoule < 2_500) {
      if (lot.vue.travail) {
        lot.vue.travail.etat = 'en_cours';
        lot.vue.travail.avancement = avancement(
          'essai',
          Math.floor(((ecoule - 800) / 1_700) * lot.vue.lignes),
          lot.vue.lignes,
          ecoule - 800,
        );
      }
      return;
    }
    const refus = lot.profil === 'refus';
    const lignes = lot.vue.lignes;
    const refuses = refus ? Math.max(2, Math.round(lignes / 10)) : 0;
    const chiffres = { total: lignes, crees: lignes - refuses, refuses };
    lot.essai =
      lot.vue.nature === 'oeuvres'
        ? rapportOeuvres(lignes, refuses, true)
        : refus
          ? rapportNature(chiffres, true, {
              erreurs: Array.from({ length: refuses }, (_, i) => ({
                ligne: 5 + i * 11,
                libelle: `Ligne d’essai ${String(i + 1)}`,
                messages: ['Une valeur obligatoire est vide.'],
              })),
            })
          : rapportNature(chiffres, true);
    lot.vue.compteurs = { ...chiffres, completes: 0, ignores: 0, ecartes: 0 };
    lot.vue.rejets = refuses > 0 ? [{ feuille: 'principal', lignes: refuses }] : [];
    lot.vue.ecartes = null;
    lot.vue.etat = refus ? 'essaye' : 'pret';
    lot.vue.rapportEssai = true;
    lot.vue.essayeLe = new Date().toISOString();
    if (lot.vue.travail) {
      lot.vue.travail.etat = 'reussi';
      lot.vue.travail.avancement = null;
      lot.vue.travail.termineLe = lot.vue.essayeLe;
    }
    lot.vue.revision += 1;
    lot.phase = null;
    return;
  }
  if (ecoule < 1_500) return;
  if (ecoule < 6_000) {
    lot.vue.etat = 'en_cours';
    if (lot.vue.travail) {
      lot.vue.travail.etat = 'en_cours';
      lot.vue.travail.avancement = avancement(
        'import',
        Math.floor(((ecoule - 1_500) / 4_500) * lot.vue.lignes),
        lot.vue.lignes,
        ecoule - 1_500,
      );
    }
    return;
  }
  const compteurs = lot.vue.compteurs;
  if (!compteurs) return;
  lot.final =
    lot.vue.nature === 'oeuvres'
      ? rapportOeuvres(compteurs.total, 0, false)
      : rapportNature(compteurs, false);
  lot.vue.etat = 'termine';
  lot.vue.rapport = true;
  lot.vue.termineLe = new Date().toISOString();
  if (lot.vue.travail) {
    lot.vue.travail.etat = 'reussi';
    lot.vue.travail.avancement = null;
    lot.vue.travail.termineLe = lot.vue.termineLe;
  }
  lot.vue.revision += 1;
  lot.phase = null;
}

function trouverLot(idGalerie: string, lotId: string): LotDemo {
  const lot = etat(idGalerie).lots.find((ligne) => ligne.vue.id === lotId);
  if (!lot) throw introuvable('Lot');
  avancer(lot);
  return lot;
}

function copie(lot: LotDemo): LotReprise {
  return structuredClone(lot.vue);
}

export const demoReprise = {
  mandat(idGalerie: string): EtatMandat {
    const { mandat } = etat(idGalerie);
    return structuredClone({
      actif: mandatActif(etat(idGalerie)),
      demande: mandat.demande,
    });
  },

  poserMandat(idGalerie: string, corps: PoseMandat): MandatVue {
    const g = etat(idGalerie);
    const actif = mandatVue({
      etat: 'actif',
      source: 'contrat',
      natures: corps.natures,
      confidentiels: corps.confidentiels,
      reference: corps.reference,
      motif: corps.motif,
      accordePar: operateurCourant(),
      accordeLe: new Date().toISOString(),
      expireLe: new Date(`${corps.expireLe}T23:59:59`).toISOString(),
    });
    exigerElevationDemo();
    g.mandat = { actif, demande: null };
    return structuredClone(actif);
  },

  demanderMandat(idGalerie: string, corps: DemandeMandat): MandatVue {
    const g = etat(idGalerie);
    if (g.mandat.demande) return structuredClone(g.mandat.demande);
    const demande = mandatVue({
      etat: 'en_attente',
      source: 'galerie',
      natures: corps.natures,
      confidentiels: corps.confidentiels,
      motif: corps.motif,
      dureeJours: corps.dureeJours,
      demandePar: operateurCourant(),
      demandeLe: new Date().toISOString(),
      expireLe: dans(7 * JOUR),
    });
    g.mandat = { ...g.mandat, demande };
    return structuredClone(demande);
  },

  retirerMandat(idGalerie: string): void {
    etat(idGalerie).mandat = { actif: null, demande: null };
  },

  lots(idGalerie: string): LotReprise[] {
    return [
      ...demoCorrespondance.lots(idGalerie),
      ...etat(idGalerie).lots.map((lot) => {
        avancer(lot);
        return copie(lot);
      }),
    ];
  },

  lot(idGalerie: string, lotId: string): LotReprise {
    if (demoCorrespondance.possede(idGalerie, lotId))
      return demoCorrespondance.lot(idGalerie, lotId);
    return copie(trouverLot(idGalerie, lotId));
  },

  deposer(idGalerie: string, depot: DepotLot): LotReprise {
    const g = etat(idGalerie);
    const fichiers = CHAMPS_PAR_NATURE[depot.nature]
      .map(({ champ }) => ({ champ, fichier: depot.fichiers[champ] }))
      .filter(
        (ligne): ligne is { champ: typeof ligne.champ; fichier: File } =>
          ligne.fichier !== undefined,
      );
    const principal = depot.fichiers.fichier;
    if (!principal)
      throw new ApiError(400, 'validation.champ_manquant', 'Joignez le tableur (CSV ou XLSX).', {
        raison: 'fichier_requis',
      });
    const lot: LotDemo = {
      vue: {
        id: nouvelId('demo-lot'),
        galerieId: idGalerie,
        nature: depot.nature,
        etat: 'depose',
        revision: 0,
        sources: fichiers.map(({ champ, fichier }) => ({
          champ,
          nom: fichier.name,
          octets: fichier.size,
        })),
        options: {
          confidentiels: depot.nature === 'documents' && depot.confidentiels,
          motifInventaire:
            depot.nature === 'oeuvres' && depot.motifInventaire.trim() !== ''
              ? depot.motifInventaire.trim()
              : null,
        },
        lignes: Math.max(1, Math.round(principal.size / 120)),
        compteurs: null,
        rapportEssai: false,
        rapport: false,
        travail: null,
        operateur: operateurCourant(),
        creeLe: new Date().toISOString(),
        essayeLe: null,
        lanceLe: null,
        termineLe: null,
      },
      essai: null,
      final: null,
      profil: /refus/i.test(principal.name) ? 'refus' : 'propre',
      phase: null,
      rapportAnnulation: null,
      defile: null,
    };
    g.lots.unshift(lot);
    return copie(lot);
  },

  /** Un lot de documents ou de factures dont le manifeste est un fichier d'un dépôt (ARTO-131). */
  depuisDepot(idGalerie: string, corps: CreationLotFichiers): LotReprise {
    const g = etat(idGalerie);
    const depot = demoDepots.depots(idGalerie).find((candidat) => candidat.id === corps.depotId);
    if (!depot) throw introuvable('Dépôt');
    if (!depot.utilisable)
      throw conflit(
        'depot_inutilisable',
        'Ce dépôt n’est pas utilisable : son inventaire n’est pas fait, ou un fichier attend l’antivirus.',
      );
    const manifeste = demoDepots
      .pageManifeste(idGalerie, corps.depotId)
      .find((fichier) => fichier.id === corps.manifesteId);
    if (!manifeste || manifeste.etat !== 'accepte')
      throw conflit('fichier_inutilisable', 'Le manifeste cité n’est pas accepté dans ce dépôt.');
    const lot: LotDemo = {
      vue: {
        id: nouvelId('demo-lot'),
        galerieId: idGalerie,
        nature: corps.nature,
        etat: 'depose',
        revision: 0,
        origine: 'depot',
        parcours: 'fichiers',
        depotId: corps.depotId,
        sources: [
          {
            champ: 'fichier',
            nom: manifeste.chemin,
            octets: manifeste.octets,
            fichierId: manifeste.id,
          },
          { champ: 'manifeste', nom: 'manifeste.json', octets: 4_096 },
        ],
        options: {
          confidentiels: corps.nature === 'documents' && corps.confidentiels,
          motifInventaire: null,
        },
        lignes: Math.max(1, Math.round(manifeste.octets / 120)),
        compteurs: null,
        rapportEssai: false,
        rapport: false,
        travail: null,
        operateur: operateurCourant(),
        creeLe: new Date().toISOString(),
        essayeLe: null,
        lanceLe: null,
        termineLe: null,
      },
      essai: null,
      final: null,
      profil: /refus/i.test(manifeste.chemin) ? 'refus' : 'propre',
      phase: null,
      rapportAnnulation: null,
      defile: null,
    };
    g.lots.unshift(lot);
    return copie(lot);
  },

  essayer(idGalerie: string, lotId: string): LotReprise {
    if (demoCorrespondance.possede(idGalerie, lotId))
      return demoCorrespondance.essayer(idGalerie, lotId);
    const g = etat(idGalerie);
    const lot = trouverLot(idGalerie, lotId);
    if (!['depose', 'essaye', 'pret'].includes(lot.vue.etat))
      throw conflit('lot_deja_lance', 'Ce lot est déjà lancé : il ne s’essaie plus.');
    if (lot.phase?.genre === 'essai')
      throw conflit('essai_en_cours', 'Un essai de ce lot est déjà en cours.');
    exigerMandat(g, lot.vue);
    lot.vue.etat = 'depose';
    lot.vue.compteurs = null;
    lot.vue.rapportEssai = false;
    lot.vue.essayeLe = null;
    lot.vue.revision += 1;
    lot.vue.travail = travail('en_attente', 'reprise:essayer');
    lot.essai = null;
    lot.phase = { genre: 'essai', depuis: Date.now() };
    return copie(lot);
  },

  lancer(idGalerie: string, lotId: string): LotReprise {
    if (demoCorrespondance.possede(idGalerie, lotId))
      return demoCorrespondance.lancer(idGalerie, lotId);
    const g = etat(idGalerie);
    const lot = trouverLot(idGalerie, lotId);
    if (lot.vue.annulation)
      throw conflit(
        'lot_annule',
        'Ce lot a été annulé : déposez un nouveau lot pour reprendre ces lignes.',
      );
    if (lot.vue.etat !== 'pret')
      throw conflit(
        'lot_non_pret',
        'Le lot doit avoir réussi son essai, sans refus, avant d’être lancé.',
      );
    if (exigeSecondFacteur(lot.vue)) exigerElevationDemo();
    exigerMandat(g, lot.vue);
    lot.vue.etat = 'enfile';
    lot.vue.lanceLe = new Date().toISOString();
    lot.vue.revision += 1;
    lot.vue.travail = travail('en_attente', `import:${lot.vue.nature}`);
    lot.phase = { genre: 'import', depuis: Date.now() };
    return copie(lot);
  },

  relancer(idGalerie: string, lotId: string): LotReprise {
    if (demoCorrespondance.possede(idGalerie, lotId))
      throw conflit('lot_non_pret', 'Seul un lot échoué se relance.');
    const g = etat(idGalerie);
    const lot = trouverLot(idGalerie, lotId);
    if (lot.vue.annulation)
      throw conflit(
        'lot_annule',
        'Ce lot a été annulé : déposez un nouveau lot pour reprendre ces lignes.',
      );
    if (lot.vue.etat !== 'echoue') throw conflit('lot_non_pret', 'Seul un lot échoué se relance.');
    if (exigeSecondFacteur(lot.vue)) exigerElevationDemo();
    exigerMandat(g, lot.vue);
    lot.vue.etat = 'enfile';
    lot.vue.revision += 1;
    lot.vue.travail = travail('en_attente', `import:${lot.vue.nature}`);
    lot.vue.termineLe = null;
    lot.phase = { genre: 'import', depuis: Date.now() };
    return copie(lot);
  },

  rapport(idGalerie: string, lotId: string, type: 'essai' | 'final'): RapportLot {
    if (demoCorrespondance.possede(idGalerie, lotId))
      return normaliserRapport(demoCorrespondance.rapport(idGalerie, lotId, type));
    const lot = trouverLot(idGalerie, lotId);
    const brut = type === 'essai' ? lot.essai : lot.final;
    if (!brut) throw introuvable('Rapport');
    return normaliserRapport(structuredClone(brut));
  },

  /** Les lots de la galerie tels que la démonstration les tient, à jour : pour le plan et l'annulation. */
  interne(idGalerie: string): LotDemo[] {
    const lots = etat(idGalerie).lots;
    lots.forEach(avancer);
    return lots;
  },

  /** Le plan lance un lot : mêmes états que `lancer`, sans second facteur (il l'a eu au lancement). */
  demarrerImport(lot: LotDemo): void {
    lot.vue.etat = 'enfile';
    lot.vue.lanceLe = new Date().toISOString();
    lot.vue.revision += 1;
    lot.vue.travail = travail('en_attente', `import:${lot.vue.nature}`);
    lot.phase = { genre: 'import', depuis: Date.now() };
  },

  /** L'annulation est demandée : elle tourne, puis rend son rapport. */
  demarrerAnnulation(lot: LotDemo, par: string): void {
    lot.vue.annulation = {
      etat: 'demandee',
      demandeLe: new Date().toISOString(),
      par,
      faitLe: null,
      compteurs: null,
      travail: travail('en_attente', 'reprise:annuler'),
    };
    lot.vue.revision += 1;
    lot.rapportAnnulation = null;
    lot.phase = { genre: 'annulation', depuis: Date.now() };
  },

  gabarit(gabarit: Gabarit): string {
    const colonnes: Record<string, string[]> = {
      oeuvres: ['Titre', 'Artiste', 'Année', 'Technique', 'Lieu', 'Numéro d’inventaire'],
      lieux: ['Nom', 'Type', 'Adresse', 'Code postal', 'Ville'],
      artistes: ['Prénom', 'Nom', 'Naissance', 'Nationalité', 'Biographie (FR)'],
      contacts: ['Nom', 'Prénom', 'Raison sociale', 'E-mail', 'Téléphone'],
      expositions: ['Titre', 'Type', 'Début', 'Fin', 'Lieu'],
      accrochages: ['Exposition', 'Numéro d’inventaire', 'Titre', 'Artiste'],
      'factures-historiques': ['Numéro', 'Date', 'Client', 'Total HT', 'Total TTC', 'Fichier PDF'],
      documents: ['Fichier', 'Titre', 'Catégorie', 'Échéance'],
      localisations: ['Référence d’origine', 'Œuvre', 'Lieu', 'Date', 'Note'],
      provenance: ['Référence d’origine', 'Œuvre', 'Type', 'Libellé', 'Contact', 'Période'],
      editions: ['Œuvre', 'Numéro', 'Type', 'Statut', 'Tirage', 'Épreuves d’artiste'],
      membres: ['Nom', 'E-mail', 'Rôle', 'Langue'],
    };
    return `${(colonnes[gabarit.feuille ?? gabarit.nature] ?? []).join(';')}\n`;
  },
};
